import type { Variant } from '@/types/custom';

export interface StockValidationParams {
    variant: Variant;
    currentCartQuantity: number;
    requestedQuantity?: number;
}

export interface StockValidationResult {
    canAdd: boolean;
    canIncrease: boolean;
    maxQuantity: number;
    isAtMaxCapacity: boolean;
    isOutOfStock: boolean;
    statusMessage: string | null;
    errorMessage: string | null;
}

/**
 * Centralized stock validation logic - single source of truth
 */
export function validateStock(
    params: StockValidationParams,
    translations: {
        outOfStock: string;
        inCart: (params: { quantity: number }) => string;
        lastItemInCart: string;
        allItemsInCart: (params: { quantity: number }) => string;
        insufficientStock: (params: { availableStock: number; currentQty: number }) => string;
        stockLimitExceededWithDetails: (params: {
            requested: number;
            available: number;
            inCart: number;
        }) => string;
    }
): StockValidationResult {
    const { variant, currentCartQuantity, requestedQuantity = 1 } = params;
    const { hasUnlimitedStock, stock, inStock } = variant;

    // If product is out of stock
    if (!inStock) {
        return {
            canAdd: false,
            canIncrease: false,
            maxQuantity: currentCartQuantity,
            isAtMaxCapacity: true,
            isOutOfStock: true,
            statusMessage: translations.outOfStock,
            errorMessage: translations.outOfStock,
        };
    }

    // If unlimited stock
    if (hasUnlimitedStock) {
        const statusMessage =
            currentCartQuantity > 0 ? translations.inCart({ quantity: currentCartQuantity }) : null;

        return {
            canAdd: true,
            canIncrease: true,
            maxQuantity: Number.POSITIVE_INFINITY,
            isAtMaxCapacity: false,
            isOutOfStock: false,
            statusMessage,
            errorMessage: null,
        };
    }

    // Limited stock logic
    const availableStock = stock || 0;
    const remainingStock = availableStock - currentCartQuantity;
    const canAddRequested = remainingStock >= requestedQuantity;
    const isAtMax = currentCartQuantity >= availableStock;

    // Status message logic
    let statusMessage: string | null = null;
    if (isAtMax) {
        statusMessage =
            availableStock === 1
                ? translations.lastItemInCart
                : translations.allItemsInCart({ quantity: availableStock });
    } else if (currentCartQuantity > 0) {
        statusMessage = translations.inCart({ quantity: currentCartQuantity });
    }

    // Error message for when trying to add more than available
    let errorMessage: string | null = null;
    if (!canAddRequested && requestedQuantity > 0) {
        if (remainingStock <= 0) {
            errorMessage = translations.insufficientStock({
                availableStock,
                currentQty: currentCartQuantity,
            });
        } else {
            errorMessage = translations.stockLimitExceededWithDetails({
                requested: requestedQuantity,
                available: remainingStock,
                inCart: currentCartQuantity,
            });
        }
    }

    return {
        canAdd: canAddRequested,
        canIncrease: remainingStock > 0,
        maxQuantity: availableStock,
        isAtMaxCapacity: isAtMax,
        isOutOfStock: false,
        statusMessage,
        errorMessage,
    };
}
